import { ApolloProvider } from '@apollo/client';
import { ServerStyleSheets, ThemeProvider } from '@material-ui/core/styles';
import difference from 'lodash/difference';
import mapValues from 'lodash/mapValues';
import moment from 'moment';
// Flex template application uses English translations as default.
import { SnackbarProvider } from 'notistack';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import ReactDOMServer from 'react-dom/server';
import { HelmetProvider } from 'react-helmet-async';
import { Provider } from 'react-redux';
import { BrowserRouter, StaticRouter } from 'react-router-dom';
import { apolloClient } from './apollo';
import config from './shopConfig/config';
import AppContext from './context/AppContext';
import routeConfiguration from './routes/routeConfiguration';
import Routes from './routes/Routes';
import configureStore from './store';
import defaultMessages from './translations/en.json';
// If you want to change the language, change the imports to match the wanted locale:
//   1) Change the language in the config.js file!
//   2) Import correct locale rules for Moment library
//   3) Use the `messagesInLocale` import to add the correct translation file.
//   4) To support older browsers we need add the correct locale for intl-relativetimeformat to `util/polyfills.js`
// Note that there is also translations in './translations/countryCodes.js' file
// This file contains ISO 3166-1 alpha-2 country codes, country names and their translations in our default languages
// This used to collect billing address in AddressForm on CheckoutPage
// Step 2:
// If you are using a non-english locale with moment library,
// you should also import time specific formatting rules for that locale
// e.g. for French: import 'moment/locale/fr';
// Step 3:
// If you are using a non-english locale, point `messagesInLocale` to correct .json file
import auMessages from './translations/en-au.json';
import caMessages from './translations/en-ca.json';
import gbMessages from './translations/en-gb.json';
import { IntlProvider } from './util/reactIntl';
import { getCountryObjectFromCodeWithDefault } from './util/countryCodes';
import { CountryCode } from './types/apollo/generated/types.generated';
import { getMuiTheme, getMuiThemeForTreetShop } from './mui';
import { setInitialHeapEventProperties } from './util/heap';

const IS_TEST_ENV = process.env.NODE_ENV === 'test';
const DEFAULT_LOCALE = 'en-US';

// If translation key is missing from `messagesInLocale` (e.g. fr.json),
// corresponding key will be added to messages from `defaultMessages` (en.json)
// to prevent missing translation key errors.
const addMissingTranslations = (sourceLangTranslations, targetLangTranslations) => {
  const sourceKeys = Object.keys(sourceLangTranslations);
  const targetKeys = Object.keys(targetLangTranslations);
  const missingKeys = difference(sourceKeys, targetKeys);

  const addMissingTranslation = (translations, missingKey) => ({
    ...translations,
    [missingKey]: sourceLangTranslations[missingKey],
  });

  return missingKeys.reduce(addMissingTranslation, targetLangTranslations);
};

const setupLocale = (locale) => {
  if (IS_TEST_ENV) {
    // Use english as a default locale in tests
    // This affects app.test.js and app.node.test.js tests
    config.locale = 'en';
    return;
  }

  // Set the Moment locale globally
  // See: http://momentjs.com/docs/#/i18n/changing-locale/
  moment.locale(locale);
};

const getLocaleMessages = (locale) => {
  let messages;
  switch (locale) {
    case 'en-AU':
      messages = addMissingTranslations(defaultMessages, auMessages);
      break;
    case 'en-CA':
      messages = addMissingTranslations(defaultMessages, caMessages);
      break;
    case 'en-GB':
      messages = addMissingTranslations(defaultMessages, gbMessages);
      break;
    case DEFAULT_LOCALE:
    default:
      messages = defaultMessages;
      break;
  }
  // Locale should not affect the tests. We ensure this by providing
  // messages with the key as the value of each message.
  const testMessages = mapValues(messages, (val, key) => key);

  return IS_TEST_ENV ? testMessages : messages;
};

const getUserLocale = (
  allowedOriginToDestinationCountries = {},
  detectedUserCountryCode = null
) => {
  let overrideLocale;
  if (typeof document !== 'undefined') {
    overrideLocale = new URLSearchParams(document.location.search).get('locale');
  }

  let usersLocaleFromCountryCode;
  if (detectedUserCountryCode) {
    usersLocaleFromCountryCode =
      getCountryObjectFromCodeWithDefault(detectedUserCountryCode).locale;
  }
  const usersLocale = Intl.DateTimeFormat().resolvedOptions().locale;
  const shopDefaultCountry = Object.keys(allowedOriginToDestinationCountries)[0];
  const shopDefaultLocale = getCountryObjectFromCodeWithDefault(
    shopDefaultCountry || CountryCode.Us
  ).locale;

  return (
    overrideLocale ||
    usersLocaleFromCountryCode ||
    usersLocale ||
    shopDefaultLocale ||
    DEFAULT_LOCALE
  );
};

export const ClientApp = (props) => {
  const { store } = props;

  const [, setClientInitialized] = useState(false);

  const {
    treetId,
    shopId,
    canonicalRootUrl,
    userAgent,
    shopConfig: shopConfigV2,
    primaryLocale,
    detectedUserCountryCode,
  } = store.getState().initial;
  const { treetShopConfig } = store.getState().TreetShopLandingPage;

  // Remove the server side css once client side has rendered and set initialized
  // Ref: https://material-ui.com/guides/server-rendering/
  useEffect(() => {
    const jssStyles = document.querySelector('#jss-server-side');
    if (jssStyles) {
      jssStyles.parentElement.removeChild(jssStyles);
    }

    // The state change triggers a re-render which ensures client-side styles are properly applied.
    // https://github.com/mui/material-ui/issues/19780
    // TODO (julianwilkinson|TREET-14846): Upgrade to MUI v5 which fixes this
    setClientInitialized(true);
  }, []);

  useEffect(() => {
    if (shopConfigV2 && userAgent) {
      // Only run client-side when heap is loaded
      setInitialHeapEventProperties(shopConfigV2, userAgent);
    }
  }, [shopConfigV2, userAgent]);

  const theme = getMuiTheme(treetId, shopConfigV2, treetShopConfig);

  const usersLocale = getUserLocale(
    shopConfigV2?.internationalConfig?.allowedOriginToDestinationCountries,
    detectedUserCountryCode
  );

  setupLocale(usersLocale);
  const localeMessages = getLocaleMessages(usersLocale);
  const resolvedLocale = usersLocale || 'en';

  return (
    <IntlProvider locale={resolvedLocale} messages={localeMessages} textComponent="span">
      <Provider store={store}>
        <AppContext.Provider
          value={{
            treetId,
            shopId,
            canonicalRootUrl,
            userAgent,
            primaryLocale,
            shopConfig: shopConfigV2,
          }}
        >
          <ThemeProvider theme={theme}>
            <SnackbarProvider maxSnack={3}>
              <ApolloProvider client={apolloClient}>
                <HelmetProvider>
                  <BrowserRouter>
                    <Routes routes={routeConfiguration(treetId)} />
                  </BrowserRouter>
                </HelmetProvider>
              </ApolloProvider>
            </SnackbarProvider>
          </ThemeProvider>
        </AppContext.Provider>
      </Provider>
    </IntlProvider>
  );
};

const { any, string } = PropTypes;

ClientApp.propTypes = { store: any.isRequired };

/**
 * Redirect Client App is used *only* for rendering the page at `/redirect`.
 *
 * `/redirect` is only hit after the IDP Authentication has concluded server-side,
 * and the window quickly closes after it is opened.
 * Rendering an extremely pared-down app ensures no external API calls
 * are made that can potentially cause errors when they are canceled at window close.
 *
 */
export const RedirectClientApp = (props) => {
  const { store } = props;
  // Dummy value, not used for anything except checking
  // if route configuration should use Treet Shop routes.
  const treetId = 'dummy';
  // Remove the server side css once client side has rendered
  // Ref: https://material-ui.com/guides/server-rendering/
  useEffect(() => {
    const jssStyles = document.querySelector('#jss-server-side');
    if (jssStyles) {
      jssStyles.parentElement.removeChild(jssStyles);
    }
  }, []);

  const theme = getMuiThemeForTreetShop();

  return (
    <Provider store={store}>
      <AppContext.Provider value={{ treetId }}>
        <ThemeProvider theme={theme}>
          <SnackbarProvider maxSnack={3}>
            <HelmetProvider>
              <BrowserRouter>
                <Routes routes={routeConfiguration(treetId)} />
              </BrowserRouter>
            </HelmetProvider>
          </SnackbarProvider>
        </ThemeProvider>
      </AppContext.Provider>
    </Provider>
  );
};

RedirectClientApp.propTypes = { store: any.isRequired };

export const ServerApp = (props) => {
  const { url, context, helmetContext, store } = props;

  const {
    treetId,
    shopId,
    canonicalRootUrl,
    userAgent,
    shopConfig,
    primaryLocale,
    detectedUserCountryCode,
  } = store.getState().initial;

  const { treetShopConfig } = store.getState().TreetShopLandingPage;

  const theme = getMuiTheme(treetId, shopConfig, treetShopConfig);

  const locale = getUserLocale(
    shopConfig?.internationalConfig?.allowedOriginToDestinationCountries,
    detectedUserCountryCode
  );
  setupLocale(locale);
  const localeMessages = getLocaleMessages(locale);

  HelmetProvider.canUseDOM = false;
  return (
    <IntlProvider locale={locale} messages={localeMessages} textComponent="span">
      <Provider store={store}>
        <AppContext.Provider
          value={{
            treetId,
            shopId,
            canonicalRootUrl,
            userAgent,
            shopConfig,
            primaryLocale,
          }}
        >
          <ThemeProvider theme={theme}>
            <SnackbarProvider maxSnack={3}>
              <ApolloProvider client={apolloClient}>
                <HelmetProvider context={helmetContext}>
                  <StaticRouter location={url} context={context}>
                    <Routes routes={routeConfiguration(treetId)} />
                  </StaticRouter>
                </HelmetProvider>
              </ApolloProvider>
            </SnackbarProvider>
          </ThemeProvider>
        </AppContext.Provider>
      </Provider>
    </IntlProvider>
  );
};

ServerApp.propTypes = { url: string.isRequired, context: any.isRequired, store: any.isRequired };

/**
 * Render the given route.
 *
 * @param {String} url Path to render
 * @param {Object} serverContext Server rendering context from react-router
 *
 * @returns {Object} Object with keys:
 *  - {String} body: Rendered application body of the given route
 *  - {Object} head: Application head metadata from react-helmet
 */
// TODO (SY): Figure out why home page goes here but not /s ?
export const renderApp = (req, url, serverContext, preloadedState, collectChunks) => {
  // Don't pass an SDK instance since we're only rendering the
  // component tree with the preloaded store state and components
  // shouldn't do any SDK calls in the (server) rendering lifecycle.

  const store = configureStore({
    ...preloadedState,
  });

  const helmetContext = {};
  const sheets = new ServerStyleSheets();

  // Render the component to a string.

  const body = ReactDOMServer.renderToString(
    collectChunks(
      sheets.collect(
        <ServerApp url={url} context={serverContext} helmetContext={helmetContext} store={store} />
      )
    )
  );

  const css = sheets.toString();
  const { helmet: head } = helmetContext;
  return { head, body, css };
};
